import React, { useEffect, useRef, useState } from "react";
import { isNotEmpty } from "../../helpers/utils";
import { CustomMuiSelectProps, MultiOptionsDataType } from "./MuiMultiSelect";
import {
  getFilteredOptionsByText,
  handleKeyDownAction,
  resetValuesAction,
} from "./helper";

export const useMuiMultiSelectHook = (props: CustomMuiSelectProps) => {
  const {
    options,
    selectedValues = [],
    reset,
    dialogWidth = 0,
    showDoubleLabel = false,
    onSelectionChange,
    onItemsChangedOrDropdownClosed,
    declinedValue,
  } = props;

  const selectRef = useRef<HTMLSelectElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number>(dialogWidth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedItem, setSelectedItem] = useState<string | number>("");
  const [selectedItems, setSelectedItems] =
    useState<Array<string | number>>(selectedValues);
  const [selectedDataChip, setSelectedDataChip] = useState<
    MultiOptionsDataType[]
  >([]);
  const itemRefs = React.useRef<HTMLLIElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
   const [declinedAll, setDeclinedAll] =
    useState<boolean>(!!declinedValue);

  // Sync selected values with options
  useEffect(() => {
    if (isNotEmpty(selectedValues)) {
      const preSelectedItems = options.filter((option) =>
        selectedValues.includes(option.value),
      );
      setSelectedDataChip(preSelectedItems);
      setSelectedItems(selectedValues);
    }
  }, [selectedValues, options]);

  // Reset selection when reset prop changes
  useEffect(() => {
    if (reset !== undefined) {
      setSelectedDataChip([]);
      setSelectedItems([]);
    }
  }, [reset]);

  // Set popover width and handle keyboard open
  useEffect(() => {
    const ref = selectRef.current;
    if (ref) {
      setPopoverWidth(ref.offsetWidth);
      ref.onkeydown = (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen(e as any);
        }
      };
    }
  }, [selectRef.current]);

  // Open popover
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Close popover and notify changes
  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
    onItemsChangedOrDropdownClosed?.(selectedItems);
    onSelectionChange?.(selectedItems);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  // Reset selected values
  const resetValues = (values?: string[]) => {
    resetValuesAction(
      options,
      setSelectedItems,
      setSelectedDataChip,
      setSelectedItem,
      values,
    );
  };

  const getFilteredOptions = React.useCallback(() => {
    return getFilteredOptionsByText(options, searchText, showDoubleLabel);
  }, [options, searchText, showDoubleLabel]);

  const filteredOptions = getFilteredOptions();

  const handleSelectAll = React.useCallback(() => {
    if (props.disabled) return;
    if (selectedItems.length === filteredOptions.length) {
      setSelectedItems([]);
      setSelectedDataChip([]);
    } else {
      const filterOptionValues = filteredOptions.map((option) => option.value);
      setSelectedItems(filterOptionValues);
      setSelectedDataChip(filteredOptions);
    }
  }, [props.disabled, selectedItems.length, filteredOptions]);

  const handleDeclineAll = () => {
    setDeclinedAll(!declinedAll);
    setSelectedItems([]);
    setSelectedDataChip([]);
  };

  const handleToggleItem = React.useCallback((option: MultiOptionsDataType) => {
    setSelectedItems((prevSelected) => {
      const isSelected = prevSelected.includes(option.value);
      const newSelectedItems = isSelected
        ? prevSelected.filter((item) => item !== option.value)
        : [...prevSelected, option.value];

      setSelectedDataChip((prevData) => {
        if (!isSelected) {
          return [...prevData, option];
        } else {
          return prevData.filter((item) => item.value !== option.value);
        }
      });

      return newSelectedItems;
    });
  }, []);

  const handleDelete = React.useCallback(
    (chipValue: number | string) => {
      const updatedDataChip = selectedDataChip.filter(
        (item) => item.value !== chipValue,
      );
      setSelectedDataChip(updatedDataChip);

      const updatedItems = selectedItems.filter((item) => item !== chipValue);
      setSelectedItems(updatedItems);

      onItemsChangedOrDropdownClosed?.(updatedItems);
      if (onSelectionChange) {
        onSelectionChange(updatedItems);
      }
    },
    [
      selectedDataChip,
      selectedItems,
      onItemsChangedOrDropdownClosed,
      onSelectionChange,
    ],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLUListElement>) => {
      handleKeyDownAction(
        event,
        filteredOptions,
        setFocusedIndex,
        itemRefs,
        focusedIndex,
      );
    },
    [filteredOptions.length, focusedIndex, itemRefs],
  );

  useEffect(() => {
    itemRefs.current[focusedIndex]?.focus();
  }, [focusedIndex]);

  return {
    selectRef,
    popoverWidth,
    anchorEl,
    searchText,
    selectedItem,
    selectedItems,
    selectedDataChip,
    itemRefs,
    focusedIndex,
    filteredOptions,
    declinedAll,
    handleOpen,
    handleClose,
    handleSearchChange,
    handleSelectAll,
    handleToggleItem,
    handleDelete,
    handleKeyDown,
    resetValues,
    setFocusedIndex,
    setAnchorEl,
    setSearchText,
    setSelectedItem,
    setSelectedItems,
    setSelectedDataChip,
    handleDeclineAll,
  };
};
